import { WorkflowRule, WorkflowExecutionLog, WorkflowStatistics } from '../types/workflowRules';

// ==========================================
// WORKFLOW RULES STORAGE SERVICE
// ==========================================

const STORAGE_KEY_RULES = 'crm_workflow_rules';
const STORAGE_KEY_LOGS = 'crm_workflow_execution_logs';
const MAX_LOGS = 1000; // Keep last 1000 executions

/**
 * Service for managing workflow rules in localStorage
 */
class WorkflowRulesStorage {

  // ==========================================
  // WORKFLOW RULES CRUD
  // ==========================================

  /**
   * Get all workflow rules
   */
  getAllRules(): WorkflowRule[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_RULES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading workflow rules:', error);
      return [];
    }
  }

  /**
   * Get workflow rules for a specific object
   */
  getRulesByObject(objectApiName: string): WorkflowRule[] {
    return this.getAllRules().filter(rule => rule.objectApiName === objectApiName);
  }

  /**
   * Get active workflow rules for a specific object
   */
  getActiveRulesByObject(objectApiName: string): WorkflowRule[] {
    return this.getAllRules().filter(
      rule => rule.objectApiName === objectApiName && rule.isActive
    );
  }

  /**
   * Get a single workflow rule by ID
   */
  getRule(id: string): WorkflowRule | null {
    const rules = this.getAllRules();
    return rules.find(rule => rule.id === id) || null;
  }

  /**
   * Create a new workflow rule
   */
  createRule(rule: Omit<WorkflowRule, 'id' | 'createdDate' | 'lastModified'>): WorkflowRule {
    const rules = this.getAllRules();

    const newRule: WorkflowRule = {
      ...rule,
      id: `wfr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdDate: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      executionCount: 0
    };

    rules.push(newRule);
    this.saveRules(rules);

    console.log('✅ Workflow rule created:', newRule.name);
    return newRule;
  }

  /**
   * Update an existing workflow rule
   */
  updateRule(id: string, updates: Partial<WorkflowRule>): WorkflowRule | null {
    const rules = this.getAllRules();
    const index = rules.findIndex(rule => rule.id === id);

    if (index === -1) {
      console.error('❌ Workflow rule not found:', id);
      return null;
    }

    rules[index] = {
      ...rules[index],
      ...updates,
      lastModified: new Date().toISOString()
    };

    this.saveRules(rules);

    console.log('✅ Workflow rule updated:', rules[index].name);
    return rules[index];
  }

  /**
   * Delete a workflow rule
   */
  deleteRule(id: string): boolean {
    const rules = this.getAllRules();
    const filteredRules = rules.filter(rule => rule.id !== id);

    if (rules.length === filteredRules.length) {
      console.error('❌ Workflow rule not found:', id);
      return false;
    }

    this.saveRules(filteredRules);

    console.log('✅ Workflow rule deleted:', id);
    return true;
  }

  /**
   * Clone a workflow rule
   */
  cloneRule(id: string): WorkflowRule | null {
    const original = this.getRule(id);

    if (!original) {
      console.error('❌ Workflow rule not found for cloning:', id);
      return null;
    }

    const cloned = this.createRule({
      ...original,
      name: `${original.name} (Copy)`,
      isActive: false, // Cloned rules start inactive
      createdBy: original.createdBy,
      lastModifiedBy: original.lastModifiedBy
    });

    console.log('✅ Workflow rule cloned:', cloned.name);
    return cloned;
  }

  /**
   * Toggle rule active status
   */
  toggleRuleStatus(id: string): WorkflowRule | null {
    const rule = this.getRule(id);

    if (!rule) {
      console.error('❌ Workflow rule not found:', id);
      return null;
    }

    return this.updateRule(id, { isActive: !rule.isActive });
  }

  // ==========================================
  // EXECUTION LOGGING
  // ==========================================

  /**
   * Log a workflow execution
   */
  logExecution(log: Omit<WorkflowExecutionLog, 'id'>): void {
    const logs = this.getExecutionLogs();

    const newLog: WorkflowExecutionLog = {
      ...log,
      id: `wflog_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    logs.push(newLog);

    // Keep only last MAX_LOGS entries
    if (logs.length > MAX_LOGS) {
      logs.splice(0, logs.length - MAX_LOGS);
    }

    this.saveExecutionLogs(logs);

    // Update rule execution count
    this.incrementExecutionCount(log.workflowRuleId);
  }

  /**
   * Get all execution logs
   */
  getExecutionLogs(): WorkflowExecutionLog[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_LOGS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading execution logs:', error);
      return [];
    }
  }

  /**
   * Get execution logs for a specific rule
   */
  getExecutionLogsByRule(ruleId: string): WorkflowExecutionLog[] {
    return this.getExecutionLogs().filter(log => log.workflowRuleId === ruleId);
  }

  /**
   * Get execution logs for a specific record
   */
  getExecutionLogsByRecord(recordId: string): WorkflowExecutionLog[] {
    return this.getExecutionLogs().filter(log => log.recordId === recordId);
  }

  /**
   * Clear old execution logs (keep last N days)
   */
  clearOldLogs(daysToKeep: number = 30): number {
    const logs = this.getExecutionLogs();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

    const filteredLogs = logs.filter(log => {
      const logDate = new Date(log.executedAt);
      return logDate >= cutoffDate;
    });

    const removedCount = logs.length - filteredLogs.length;
    this.saveExecutionLogs(filteredLogs);

    console.log(`✅ Cleared ${removedCount} old execution logs`);
    return removedCount;
  }

  // ==========================================
  // STATISTICS
  // ==========================================

  /**
   * Get workflow statistics
   */
  getStatistics(): WorkflowStatistics {
    const rules = this.getAllRules();
    const logs = this.getExecutionLogs();

    const activeRules = rules.filter(r => r.isActive).length;
    const successfulExecutions = logs.filter(l => l.criteriaEvaluationResult).length;
    const failedExecutions = logs.filter(l => !l.criteriaEvaluationResult || l.errors?.length).length;

    // Count executions by object
    const executionsByObject: Record<string, number> = {};
    logs.forEach(log => {
      executionsByObject[log.objectApiName] = (executionsByObject[log.objectApiName] || 0) + 1;
    });

    // Find most triggered rules
    const ruleExecutionCounts: Record<string, { name: string; count: number }> = {};
    logs.forEach(log => {
      if (!ruleExecutionCounts[log.workflowRuleId]) {
        ruleExecutionCounts[log.workflowRuleId] = {
          name: log.workflowRuleName,
          count: 0
        };
      }
      ruleExecutionCounts[log.workflowRuleId].count++;
    });

    const mostTriggeredRules = Object.entries(ruleExecutionCounts)
      .map(([ruleId, data]) => ({ ruleId, ruleName: data.name, count: data.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalRules: rules.length,
      activeRules,
      inactiveRules: rules.length - activeRules,
      totalExecutions: logs.length,
      successfulExecutions,
      failedExecutions,
      executionsByObject,
      mostTriggeredRules
    };
  }

  // ==========================================
  // PRIVATE HELPERS
  // ==========================================

  private saveRules(rules: WorkflowRule[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_RULES, JSON.stringify(rules));
    } catch (error) {
      console.error('Error saving workflow rules:', error);
    }
  }

  private saveExecutionLogs(logs: WorkflowExecutionLog[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_LOGS, JSON.stringify(logs));
    } catch (error) {
      console.error('Error saving execution logs:', error);
    }
  }

  private incrementExecutionCount(ruleId: string): void {
    const rule = this.getRule(ruleId);
    if (rule) {
      this.updateRule(ruleId, {
        executionCount: (rule.executionCount || 0) + 1,
        lastExecuted: new Date().toISOString()
      });
    }
  }

  // ==========================================
  // DATA MANAGEMENT
  // ==========================================

  /**
   * Export all workflow rules to JSON
   */
  exportRules(): string {
    const rules = this.getAllRules();
    return JSON.stringify(rules, null, 2);
  }

  /**
   * Import workflow rules from JSON
   */
  importRules(jsonData: string): { success: number; errors: string[] } {
    try {
      const imported: WorkflowRule[] = JSON.parse(jsonData);
      const rules = this.getAllRules();
      const errors: string[] = [];
      let successCount = 0;

      imported.forEach(rule => {
        try {
          // Generate new ID to avoid conflicts
          const newRule = this.createRule({
            ...rule,
            name: `${rule.name} (Imported)`
          });
          successCount++;
        } catch (error) {
          errors.push(`Failed to import rule: ${rule.name}`);
        }
      });

      return { success: successCount, errors };
    } catch (error) {
      return { success: 0, errors: ['Invalid JSON format'] };
    }
  }

  /**
   * Reset all workflow rules (DANGER!)
   * Note: Confirmation should be handled by the calling component
   */
  resetAllRules(): void {
    localStorage.removeItem(STORAGE_KEY_RULES);
    localStorage.removeItem(STORAGE_KEY_LOGS);
    console.log('✅ All workflow rules and logs cleared');
  }
}

// Export singleton instance
export const workflowRulesStorage = new WorkflowRulesStorage();
